import { createRoot, useState, useEffect, useReducer, useRef, useCallback } from '@wordpress/element';
import './view.css';

const { restUrl, nonce, maxUploadBytes } = window.sienciZendeskConfig || {};
const MAX_UPLOAD_MB = maxUploadBytes ? Math.round( maxUploadBytes / ( 1024 * 1024 ) ) : 20;

// ── Session identity ─────────────────────────────────────────────────────────
// One ID per browser session-storage lifetime (same lifetime as the form-state
// persistence below), so every analytics event from a single visit — start,
// deflection, submission — can be tied together, and re-selecting a product
// after Back/"Change" isn't miscounted as a second form start.

const SESSION_ID_KEY = 'ssf_session_id';

function getSessionId() {
	try {
		let id = sessionStorage.getItem( SESSION_ID_KEY );
		if ( ! id ) {
			id = crypto.randomUUID ? crypto.randomUUID() : `${ Date.now() }-${ Math.random().toString( 36 ).slice( 2 ) }`;
			sessionStorage.setItem( SESSION_ID_KEY, id );
		}
		return id;
	} catch ( _ ) {
		return '';
	}
}

// ── API helpers ───────────────────────────────────────────────────────────────

async function apiFetch( path, options = {} ) {
	const res = await fetch( restUrl + path, {
		headers: {
			'X-WP-Nonce': nonce,
			...( options.body && ! ( options.body instanceof FormData )
				? { 'Content-Type': 'application/json' }
				: {} ),
			...( options.headers || {} ),
		},
		...options,
	} );
	const json = await res.json().catch( () => null );
	if ( ! res.ok ) throw json || { message: 'Request failed.' };
	return json;
}

const api = {
	getIssueTypes:   ( productId ) => apiFetch( `products/${ productId }/issue-types` ),
	getIssueType:    ( id )         => apiFetch( `issue-types/${ id }` ),
	searchArticles:  ( q )          => apiFetch( `search-articles?q=${ encodeURIComponent( q ) }&per_page=6` ),
	uploadFile:      ( formData )   => apiFetch( 'upload', { method: 'POST', body: formData } ),
	submitTicket:    ( data )       => apiFetch( 'submit-ticket', { method: 'POST', body: JSON.stringify( data ) } ),
	/** Fire-and-forget analytics event — failures are intentionally silenced. */
	logEvent: ( eventType, productId = 0, issueTypeId = 0, description = '' ) =>
		apiFetch( 'log-event', {
			method: 'POST',
			body: JSON.stringify( {
				event_type:    eventType,
				product_id:    productId,
				issue_type_id: issueTypeId,
				description,
				session_id:    getSessionId(),
			} ),
		} ).catch( () => {} ),
};

// ── State management ──────────────────────────────────────────────────────────

const STEPS = { PRODUCT: 1, ISSUE_TYPE: 2, DESCRIBE: 3, DEFLECT: 4, FIELDS: 5, SUCCESS: 6 };

const STORAGE_KEY = 'ssf_form_state';

const init = ( products ) => {
	const base = {
		step:          STEPS.PRODUCT,
		products,
		product:       null,
		issueTypes:    [],
		issueType:     null,
		issueTypeData: null,   // { articles, fields } loaded when issue type chosen
		description:   '',
		contact:       { name: '', email: '', orderNumber: '' },
		fieldValues:   {},
		articleClicks: [],     // suggested/search articles the user clicked in Step 4
		uploads:       [],     // [{ file, localUrl }]  — not persisted
		uploadTokens:  [],     // Zendesk tokens (populated on submit)
		loading:       false,
		submitting:    false,
		error:         null,
		ticket:        null,   // { id, url } after success
	};

	try {
		const saved = sessionStorage.getItem( STORAGE_KEY );
		if ( saved ) {
			const { step, product, issueTypes, issueType, description, contact, fieldValues, articleClicks } = JSON.parse( saved );
			// Only restore if the saved product is still in the current product list.
			if ( product && products.some( ( p ) => p.id === product.id ) ) {
				return { ...base, step, product, issueTypes: issueTypes ?? [], issueType, description, contact, fieldValues, articleClicks: articleClicks ?? [] };
			}
		}
	} catch ( _ ) {}

	return base;
};

function reducer( state, action ) {
	switch ( action.type ) {
		case 'SELECT_PRODUCT':
			return { ...state, product: action.product, issueTypes: [], issueType: null, issueTypeData: null, step: STEPS.ISSUE_TYPE };
		case 'SET_ISSUE_TYPES':
			return { ...state, issueTypes: action.data, loading: false };
		case 'SELECT_ISSUE_TYPE':
			return { ...state, issueType: action.issueType, issueTypeData: null, fieldValues: {}, uploads: [], step: STEPS.DESCRIBE };
		case 'SET_ISSUE_TYPE_DATA':
			return { ...state, issueTypeData: action.data };
		case 'SET_DESCRIPTION':
			return { ...state, description: action.value };
		case 'SET_CONTACT':
			return { ...state, contact: { ...state.contact, ...action.values } };
		case 'SET_FIELD':
			return { ...state, fieldValues: { ...state.fieldValues, [ action.key ]: action.value } };
		case 'ARTICLE_CLICKED':
			return state.articleClicks.some( ( a ) => a.url === action.article.url && a.title === action.article.title )
				? state
				: { ...state, articleClicks: [ ...state.articleClicks, action.article ] };
		case 'ADD_UPLOAD':
			return { ...state, uploads: [ ...state.uploads, action.item ] };
		case 'REMOVE_UPLOAD':
			return { ...state, uploads: state.uploads.filter( ( _, i ) => i !== action.index ) };
		case 'NEXT':
			return { ...state, step: state.step + 1, error: null };
		case 'BACK':
			return { ...state, step: state.step - 1, error: null };
		case 'GO_TO_STEP':
			return { ...state, step: action.step, error: null };
		case 'LOADING':
			return { ...state, loading: action.value };
		case 'SUBMITTING':
			return { ...state, submitting: action.value };
		case 'ERROR':
			return { ...state, error: action.message, loading: false, submitting: false };
		case 'SUCCESS':
			return { ...state, ticket: action.ticket, step: STEPS.SUCCESS, submitting: false };
		default:
			return state;
	}
}

// ── Skeleton loaders ──────────────────────────────────────────────────────────

function SkeletonIssueTypes( { count = 5 } ) {
	return (
		<div className="ssf-issue-types">
			{ Array.from( { length: count } ).map( ( _, i ) => (
				<div key={ i } className="ssf-skeleton ssf-skeleton--issue-btn" aria-hidden="true" />
			) ) }
		</div>
	);
}

function SkeletonArticles( { count = 3 } ) {
	return (
		<>
			{ Array.from( { length: count } ).map( ( _, i ) => (
				<div key={ i } className="ssf-skeleton ssf-skeleton--article" aria-hidden="true" />
			) ) }
		</>
	);
}

// ── Step components ───────────────────────────────────────────────────────────

function ProgressBar( { step, total = 5 } ) {
	const labels = [ 'Product', 'Issue', 'Describe', 'Articles', 'Details' ];
	return (
		<div className="ssf-progress" role="list" aria-label="Form steps">
			{ labels.map( ( label, i ) => {
				const n = i + 1;
				const state = n < step ? 'done' : n === step ? 'active' : 'todo';
				return (
					<div key={ n } className={ `ssf-progress__step ssf-progress__step--${ state }` } role="listitem">
						<div className="ssf-progress__dot">
							{ state === 'done' ? '✓' : n }
						</div>
						<span className="ssf-progress__label">{ label }</span>
						{ i < labels.length - 1 && <div className="ssf-progress__line" aria-hidden="true" /> }
					</div>
				);
			} ) }
		</div>
	);
}

function NavButtons( { onBack, onNext, nextLabel = 'Continue', nextDisabled = false, loading = false } ) {
	return (
		<div className="ssf-nav">
			{ onBack && (
				<button className="ssf-btn ssf-btn--secondary" type="button" onClick={ onBack }>
					← Back
				</button>
			) }
			{ onNext && (
				<button
					className="ssf-btn ssf-btn--primary"
					type="button"
					onClick={ onNext }
					disabled={ nextDisabled || loading }
				>
					{ loading ? <span className="ssf-spinner" /> : nextLabel }
				</button>
			) }
		</div>
	);
}

// ── Selection breadcrumb ──────────────────────────────────────────────────────

function SelectionCrumbs( { state, dispatch } ) {
	const { step, product, issueType, description } = state;

	if ( step <= STEPS.PRODUCT || step === STEPS.SUCCESS ) return null;

	const crumbs = [];

	if ( product ) {
		crumbs.push( {
			icon:    product.icon,
			label:   product.label,
			onEdit:  () => dispatch( { type: 'GO_TO_STEP', step: STEPS.PRODUCT } ),
		} );
	}

	if ( issueType && step > STEPS.ISSUE_TYPE ) {
		crumbs.push( {
			icon:   issueType.icon,
			label:  issueType.label,
			onEdit: () => dispatch( { type: 'GO_TO_STEP', step: STEPS.ISSUE_TYPE } ),
		} );
	}

	if ( crumbs.length === 0 ) return null;

	return (
		<nav className="ssf-crumbs" aria-label="Selections so far">
			{ crumbs.map( ( crumb, i ) => (
				<span key={ i } className="ssf-crumbs__item">
					{ i > 0 && <span className="ssf-crumbs__sep" aria-hidden="true" /> }
					<span className="ssf-crumbs__chip">
						{ crumb.icon && (
							<svg className="ssf-crumbs__icon" aria-hidden="true">
								<use href={ `#${ crumb.icon }` } />
							</svg>
						) }
						<span className="ssf-crumbs__label">{ crumb.label }</span>
						<button
							type="button"
							className="ssf-crumbs__change"
							onClick={ crumb.onEdit }
							aria-label={ `Change ${ crumb.label }` }
						>
							Change
						</button>
					</span>
				</span>
			) ) }
		</nav>
	);
}

// ── Step 1: Product selection ─────────────────────────────────────────────────

function StepProduct( { products, dispatch } ) {
	return (
		<div className="ssf-step">
			<h2 className="ssf-step__heading">What product do you need help with?</h2>
			<div className="ssf-products">
				{ products.map( ( p ) => (
					<button
						key={ p.id }
						className="ssf-product-card"
						type="button"
						onClick={ () => {
						api.logEvent( 'started', p.id );
						dispatch( { type: 'SELECT_PRODUCT', product: p } );
					} }
					>
						{ p.icon && (
							<svg className="ssf-product-card__icon" aria-hidden="true"><use href={ `#${ p.icon }` } /></svg>
						) }
						<span className="ssf-product-card__label">{ p.label }</span>
						<span className="ssf-product-card__arrow">→</span>
					</button>
				) ) }
			</div>
		</div>
	);
}

// ── Step 2: Issue type selection ──────────────────────────────────────────────

function StepIssueType( { state, dispatch } ) {
	const { product, issueTypes, loading } = state;

	useEffect( () => {
		dispatch( { type: 'LOADING', value: true } );
		api.getIssueTypes( product.id )
			.then( ( data ) => dispatch( { type: 'SET_ISSUE_TYPES', data } ) )
			.catch( () => dispatch( { type: 'ERROR', message: 'Could not load issue types.' } ) );
	}, [ product.id ] );

	return (
		<div className="ssf-step">
			<h2 className="ssf-step__heading">What kind of issue are you experiencing?</h2>
			<p className="ssf-step__sub">{ product.label }</p>
			{ loading ? (
				<SkeletonIssueTypes />
			) : (
				<div className="ssf-issue-types">
					{ issueTypes.map( ( it ) => (
						<button
							key={ it.id }
							className="ssf-issue-btn"
							type="button"
							onClick={ () => {
								dispatch( { type: 'SELECT_ISSUE_TYPE', issueType: it } );
								api.logEvent( 'step_reached', state.product?.id, it.id, 'describe' );
								// Preload issue type data in the background
								api.getIssueType( it.id )
									.then( ( data ) => dispatch( { type: 'SET_ISSUE_TYPE_DATA', data } ) )
									.catch( () => {} );
							} }
						>
							{ it.icon && (
								<svg className="ssf-issue-btn__icon" aria-hidden="true"><use href={ `#${ it.icon }` } /></svg>
							) }
							{ it.label }
						</button>
					) ) }
				</div>
			) }
			<NavButtons onBack={ () => dispatch( { type: 'BACK' } ) } />
		</div>
	);
}

// ── Step 3: Describe the issue ────────────────────────────────────────────────

function StepDescribe( { state, dispatch } ) {
	const { description, issueType } = state;
	const valid = description.trim().length >= 10;

	return (
		<div className="ssf-step">
			<h2 className="ssf-step__heading">Briefly describe your issue</h2>
			<p className="ssf-step__sub">{ issueType.label }</p>
			<div className="ssf-field">
				<label className="ssf-label" htmlFor="ssf-description">
					Description <span className="ssf-required" aria-hidden="true">*</span>
				</label>
				<textarea
					id="ssf-description"
					className="ssf-textarea"
					rows={ 5 }
					placeholder="e.g. My machine won't connect to gSender — the controller LED is solid red…"
					value={ description }
					onChange={ ( e ) => dispatch( { type: 'SET_DESCRIPTION', value: e.target.value } ) }
				/>
				{ description.trim().length < 10 && (
					<p className="ssf-hint">{ 10 - description.trim().length } more characters needed</p>
				) }
			</div>
			<NavButtons
				onBack={ () => dispatch( { type: 'BACK' } ) }
				onNext={ () => {
					dispatch( { type: 'NEXT' } );
					api.logEvent( 'step_reached', state.product?.id, issueType?.id, 'deflect' );
				} }
				nextDisabled={ ! valid }
			/>
		</div>
	);
}

// ── Step 4: Article deflection ────────────────────────────────────────────────

function ArticleLink( { title, url, source, onClick } ) {
	return (
		<a
			className="ssf-article"
			href={ url || '#' }
			target={ url ? '_blank' : undefined }
			rel="noreferrer"
			onClick={ () => onClick && onClick( { title, url, source: source || 'Suggested' } ) }
		>
			<span className="ssf-article__title">{ title }</span>
			{ source && <span className="ssf-article__source">{ source }</span> }
			<span className="ssf-article__arrow" aria-hidden="true">↗</span>
		</a>
	);
}

function StepDeflect( { state, dispatch } ) {
	const { description, issueTypeData } = state;
	const [ query, setQuery ]           = useState( description );
	const [ zdResults, setZdResults ]   = useState( [] );
	const [ searching, setSearching ]   = useState( false );
	const [ searchError, setSearchError ] = useState( false );
	const timerRef = useRef( null );

	const runSearch = useCallback( ( q ) => {
		if ( ! q.trim() ) { setZdResults( [] ); return; }
		setSearching( true );
		setSearchError( false );
		api.searchArticles( q )
			.then( ( r ) => { setZdResults( r ); setSearching( false ); } )
			.catch( () => { setSearchError( true ); setSearching( false ); } );
	}, [] );

	// Trigger search on first render with the user's description.
	useEffect( () => { runSearch( description ); }, [] );

	// Debounce search as user refines query.
	useEffect( () => {
		clearTimeout( timerRef.current );
		timerRef.current = setTimeout( () => runSearch( query ), 400 );
		return () => clearTimeout( timerRef.current );
	}, [ query, runSearch ] );

	const curated        = issueTypeData?.articles ?? [];
	const curatedLoading = ! issueTypeData;

	// Recorded immediately on click (not just attached to a submitted ticket)
	// so we can see article engagement even when the visitor never confirms
	// via "Yes, I'm all set!" or submits — e.g. they solve it and just leave.
	const handleArticleClick = ( article ) => {
		dispatch( { type: 'ARTICLE_CLICKED', article } );
		api.logEvent( 'article_clicked', state.product?.id, state.issueType?.id, article.title );
	};

	return (
		<div className="ssf-step">
			<h2 className="ssf-step__heading">Before you submit — these might help</h2>
			<p className="ssf-step__sub">
				We found some articles based on your description. Did any of these solve your issue?
			</p>

			{ /* Search refinement */ }
			<div className="ssf-deflect-search">
				<input
					className="ssf-input"
					type="search"
					value={ query }
					onChange={ ( e ) => setQuery( e.target.value ) }
					placeholder="Refine your search…"
					aria-label="Search help articles"
				/>
			</div>

			<div className="ssf-deflect-columns">
				{ /* Curated articles — skeleton while preload is still in flight */ }
				<div className="ssf-deflect-section">
					<h3 className="ssf-deflect-section__heading">Suggested articles</h3>
					{ curatedLoading ? (
						<SkeletonArticles count={ 3 } />
					) : curated.length > 0 ? (
						curated.map( ( a ) => (
							<ArticleLink
								key={ a.id }
								title={ a.title }
								url={ a.url }
								onClick={ handleArticleClick }
							/>
						) )
					) : (
						<p className="ssf-hint">No suggested articles for this issue type.</p>
					) }
				</div>

				{ /* Live Zendesk results — skeleton while search is running */ }
				<div className="ssf-deflect-section">
					<h3 className="ssf-deflect-section__heading">Help centre search</h3>
					{ searching ? (
						<SkeletonArticles count={ 4 } />
					) : searchError ? (
						<p className="ssf-hint">Live search unavailable — browse the articles above.</p>
					) : zdResults.length === 0 && query.trim() ? (
						<p className="ssf-hint">No results found.</p>
					) : (
						zdResults.map( ( r ) => (
							<ArticleLink
								key={ r.id }
								title={ r.title }
								url={ r.url }
								source="Help Article"
								onClick={ handleArticleClick }
							/>
						) )
					) }
				</div>
			</div>

			<div className="ssf-deflect-actions">
				<p className="ssf-deflect-actions__question">Did one of these solve your issue?</p>
				<button
					className="ssf-btn ssf-btn--success"
					type="button"
					onClick={ () => {
						api.logEvent( 'deflected', state.product?.id, state.issueType?.id, state.description );
						dispatch( { type: 'SUCCESS', ticket: null } );
					} }
				>
					Yes, I'm all set!
				</button>
				<button
					className="ssf-btn ssf-btn--secondary"
					type="button"
					onClick={ () => {
						dispatch( { type: 'NEXT' } );
						api.logEvent( 'step_reached', state.product?.id, state.issueType?.id, 'fields' );
					} }
				>
					No, I still need help →
				</button>
			</div>

			<NavButtons onBack={ () => dispatch( { type: 'BACK' } ) } />
		</div>
	);
}

// ── Step 5: Contact + dynamic fields ─────────────────────────────────────────

function FieldInput( { field, value, onChange } ) {
	const id            = `ssf-field-${ field.field_key }`;
	const helpId        = field.help_text ? `${ id }-help` : undefined;
	const showRequired  = field.required || field.soft_required;

	const helpText = field.help_text && (
		<p id={ helpId } className="ssf-hint">{ field.help_text }</p>
	);

	if ( field.field_type === 'textarea' ) {
		return (
			<div className="ssf-field">
				<label className="ssf-label" htmlFor={ id }>
					{ field.label }
					{ showRequired && <span className="ssf-required" aria-hidden="true"> *</span> }
				</label>
				<textarea
					id={ id }
					className="ssf-textarea"
					rows={ 4 }
					placeholder={ field.placeholder }
					value={ value || '' }
					onChange={ ( e ) => onChange( e.target.value ) }
					required={ field.required }
					aria-describedby={ helpId }
				/>
				{ helpText }
			</div>
		);
	}

	if ( field.field_type === 'select' ) {
		const options = Array.isArray( field.field_options ) ? field.field_options : [];
		return (
			<div className="ssf-field">
				<label className="ssf-label" htmlFor={ id }>
					{ field.label }
					{ showRequired && <span className="ssf-required" aria-hidden="true"> *</span> }
				</label>
				<select
					id={ id }
					className="ssf-select"
					value={ value || '' }
					onChange={ ( e ) => onChange( e.target.value ) }
					required={ field.required }
					aria-describedby={ helpId }
				>
					<option value="">— Select —</option>
					{ options.map( ( opt ) => (
						<option key={ opt } value={ opt }>{ opt }</option>
					) ) }
				</select>
				{ helpText }
			</div>
		);
	}

	if ( field.field_type === 'upload' ) {
		return null; // Uploads handled separately below.
	}

	// Default: text / email / tel
	return (
		<div className="ssf-field">
			<label className="ssf-label" htmlFor={ id }>
				{ field.label }
				{ showRequired && <span className="ssf-required" aria-hidden="true"> *</span> }
			</label>
			<input
				id={ id }
				type={ field.field_type === 'email' ? 'email' : field.field_type === 'tel' ? 'tel' : 'text' }
				className="ssf-input"
				placeholder={ field.placeholder }
				value={ value || '' }
				onChange={ ( e ) => onChange( e.target.value ) }
				required={ field.required }
				aria-describedby={ helpId }
			/>
			{ helpText }
		</div>
	);
}

function UploadArea( { uploads, onAdd, onRemove, label = 'Screenshots / videos / files', required = false, softRequired = false, helpText = null, fieldKey = null, accept = null } ) {
	const inputRef = useRef( null );
	const showRequired = required || softRequired;

	// Only show uploads that belong to this area.
	const myUploads = fieldKey
		? uploads.filter( ( u ) => u.fieldKey === fieldKey )
		: uploads.filter( ( u ) => ! u.fieldKey );

	function handleFiles( fileList ) {
		Array.from( fileList ).forEach( ( file ) => {
			onAdd( { file, localUrl: URL.createObjectURL( file ), fieldKey } );
		} );
	}

	function handleDrop( e ) {
		e.preventDefault();
		handleFiles( e.dataTransfer.files );
	}

	return (
		<div className="ssf-field">
			<span className="ssf-label">
				{ label }
				{ showRequired
					? <span className="ssf-required" aria-hidden="true"> *</span>
					: <span className="ssf-hint-inline"> (optional)</span>
				}
			</span>
			{ helpText && <p className="ssf-hint">{ helpText }</p> }
			<div
				className="ssf-upload-area"
				onClick={ () => inputRef.current?.click() }
				onDrop={ handleDrop }
				onDragOver={ ( e ) => e.preventDefault() }
				role="button"
				tabIndex={ 0 }
				onKeyDown={ ( e ) => e.key === 'Enter' && inputRef.current?.click() }
				aria-label="Upload files — click or drag and drop"
			>
				<input
					ref={ inputRef }
					type="file"
					multiple
					accept={ accept && accept.length ? accept.join( ',' ) : 'image/*,video/mp4,video/quicktime,application/pdf' }
					className="ssf-upload-area__input"
					onChange={ ( e ) => handleFiles( e.target.files ) }
					onClick={ ( e ) => e.stopPropagation() }
					tabIndex={ -1 }
				/>
				<span className="ssf-upload-area__icon" aria-hidden="true">📎</span>
				<span>Click or drag files here</span>
				<span className="ssf-hint">
					{ accept && accept.length
						? `Accepted: ${ accept.join( ', ' ) } — max ${ MAX_UPLOAD_MB } MB each`
						: `Images, videos, PDFs — max ${ MAX_UPLOAD_MB } MB each`
					}
				</span>
			</div>
			{ myUploads.length > 0 && (
				<ul className="ssf-upload-list">
					{ myUploads.map( ( u ) => {
						// Find the true index in the full uploads array for removal.
						const globalIndex = uploads.indexOf( u );
						return (
							<li key={ globalIndex } className="ssf-upload-item">
								{ u.file.type.startsWith( 'image/' ) && (
									<img src={ u.localUrl } alt="" className="ssf-upload-item__thumb" />
								) }
								<span className="ssf-upload-item__name">{ u.file.name }</span>
								<span className="ssf-upload-item__size">
									{ ( u.file.size / 1024 / 1024 ).toFixed( 1 ) } MB
								</span>
								<button
									type="button"
									className="ssf-upload-item__remove"
									onClick={ () => onRemove( globalIndex ) }
									aria-label={ `Remove ${ u.file.name }` }
								>✕</button>
							</li>
						);
					} ) }
				</ul>
			) }
		</div>
	);
}

function StepFields( { state, dispatch } ) {
	const { contact, fieldValues, uploads, issueTypeData, submitting, error } = state;

	const fields        = issueTypeData?.fields ?? [];
	const uploadFields  = fields.filter( ( f ) => f.field_type === 'upload' );
	const otherFields   = fields.filter( ( f ) => f.field_type !== 'upload' );

	function isValid() {
		if ( ! contact.name.trim() || ! contact.email.trim() ) return false;
		for ( const f of otherFields ) {
			if ( f.required && ! ( fieldValues[ f.field_key ] || '' ).trim() ) return false;
		}
		return true;
	}

	async function handleSubmit() {
		if ( submitting ) return;
		dispatch( { type: 'SUBMITTING', value: true } );
		try {
			// Upload each file and collect Zendesk tokens.
			const tokens = [];
			for ( const { file } of uploads ) {
				const formData = new FormData();
				formData.append( 'file', file );
				const result = await api.uploadFile( formData );
				tokens.push( result.token );
			}

			// Submit the ticket.
			const ticket = await api.submitTicket( {
				name:          contact.name,
				email:         contact.email,
				order_number:  contact.orderNumber,
				product_id:    state.product?.id,
				issue_type_id: state.issueType?.id,
				description:   state.description,
				fields:        fieldValues,
				upload_tokens: tokens,
				clicked_articles: state.articleClicks,
			} );

			api.logEvent( 'submitted', state.product?.id, state.issueType?.id, state.description );
			dispatch( { type: 'SUCCESS', ticket } );
		} catch ( err ) {
			dispatch( { type: 'ERROR', message: err?.message || 'Submission failed. Please try again.' } );
		}
	}

	return (
		<div className="ssf-step">
			<h2 className="ssf-step__heading">Tell us a bit more</h2>

			{ /* Contact info — always required */ }
			<fieldset className="ssf-fieldset">
				<legend className="ssf-fieldset__legend">Your contact information</legend>
				<div className="ssf-field-grid">
					<div className="ssf-field">
						<label className="ssf-label" htmlFor="ssf-name">
							Name <span className="ssf-required" aria-hidden="true">*</span>
						</label>
						<input
							id="ssf-name"
							type="text"
							className="ssf-input"
							value={ contact.name }
							onChange={ ( e ) => dispatch( { type: 'SET_CONTACT', values: { name: e.target.value } } ) }
							placeholder="Your full name"
							autoComplete="name"
						/>
					</div>
					<div className="ssf-field">
						<label className="ssf-label" htmlFor="ssf-email">
							Email <span className="ssf-required" aria-hidden="true">*</span>
						</label>
						<input
							id="ssf-email"
							type="email"
							className="ssf-input"
							value={ contact.email }
							onChange={ ( e ) => dispatch( { type: 'SET_CONTACT', values: { email: e.target.value } } ) }
							placeholder="you@example.com"
							autoComplete="email"
						/>
					</div>
					<div className="ssf-field">
						<label className="ssf-label" htmlFor="ssf-order">
							Order number <span className="ssf-hint-inline">(optional)</span>
						</label>
						<input
							id="ssf-order"
							type="text"
							className="ssf-input"
							value={ contact.orderNumber }
							onChange={ ( e ) => dispatch( { type: 'SET_CONTACT', values: { orderNumber: e.target.value } } ) }
							placeholder="e.g. SL-12345"
						/>
					</div>
				</div>
			</fieldset>

			{ /* Issue-type-specific fields */ }
			{ otherFields.length > 0 && (
				<fieldset className="ssf-fieldset">
					<legend className="ssf-fieldset__legend">Issue details</legend>
					{ otherFields.map( ( f ) => (
						<FieldInput
							key={ f.field_key }
							field={ f }
							value={ fieldValues[ f.field_key ] }
							onChange={ ( v ) => dispatch( { type: 'SET_FIELD', key: f.field_key, value: v } ) }
						/>
					) ) }
				</fieldset>
			) }

			{ /* Upload fields — one area per upload field defined in the form builder */ }
			{ uploadFields.map( ( f ) => (
				<UploadArea
					key={ f.field_key }
					fieldKey={ f.field_key }
					label={ f.label }
					required={ f.required }
					softRequired={ f.soft_required }
					helpText={ f.help_text }
					accept={ Array.isArray( f.field_options ) && f.field_options.length ? f.field_options : null }
					uploads={ uploads }
					onAdd={ ( item ) => dispatch( { type: 'ADD_UPLOAD', item } ) }
					onRemove={ ( i )  => dispatch( { type: 'REMOVE_UPLOAD', index: i } ) }
				/>
			) ) }

			{ error && <p className="ssf-error" role="alert">{ error }</p> }

			<NavButtons
				onBack={ () => dispatch( { type: 'BACK' } ) }
				onNext={ handleSubmit }
				nextLabel="Submit ticket"
				nextDisabled={ ! isValid() }
				loading={ submitting }
			/>

			<p className="ssf-hint ssf-hint--center">
				Your information is used only to respond to your support request.
			</p>
		</div>
	);
}

// ── Step 6: Success ───────────────────────────────────────────────────────────

function StepSuccess( { ticket } ) {
	// ticket is null when the user resolved via the deflection step.
	const selfResolved = ! ticket || ! ticket.id || ticket.id === '—';

	return (
    <div className="ssf-step ssf-step--success">
      <div className="ssf-success-icon" aria-hidden="true">
        {selfResolved ? "🎉" : "✅"}
      </div>
      {selfResolved ? (
        <>
          <h2 className="ssf-step__heading">Great — glad that helped!</h2>
          <p>
            If you run into the issue again, don't hesitate to submit a support
            ticket.
          </p>
        </>
      ) : (
        <>
          <h2 className="ssf-step__heading">Ticket submitted!</h2>
          <p>
            We've received your request
            {ticket?.id && ticket.id !== "—" ? ` (#${ticket.id})` : ""} and will
            get back to you at the email you provided.
          </p>
          <p>Check your spam folder if you don't see our response.</p>
        </>
      )}
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

function SupportForm( { products } ) {
	const [ state, dispatch ] = useReducer( reducer, products, init );
	const { step, error }     = state;

	const showProgress = step >= STEPS.PRODUCT && step < STEPS.SUCCESS;

	// When restoring from sessionStorage the issueTypeData is never cached, so
	// re-fetch it any time we have an issueType but no issueTypeData.
	useEffect( () => {
		if ( state.issueType && ! state.issueTypeData ) {
			api.getIssueType( state.issueType.id )
				.then( ( data ) => dispatch( { type: 'SET_ISSUE_TYPE_DATA', data } ) )
				.catch( () => {} );
		}
	}, [ state.issueType ] );

	// Persist serialisable state to sessionStorage on every change; clear on success.
	useEffect( () => {
		if ( step === STEPS.SUCCESS ) {
			sessionStorage.removeItem( STORAGE_KEY );
			return;
		}
		try {
			const { product, issueTypes, issueType, description, contact, fieldValues, articleClicks } = state;
			// issueTypeData is intentionally excluded — it's re-fetched on load so
			// field config changes in the admin are always reflected immediately.
			sessionStorage.setItem( STORAGE_KEY, JSON.stringify( { step, product, issueTypes, issueType, description, contact, fieldValues, articleClicks } ) );
		} catch ( _ ) {}
	}, [ state ] );

	// Push a history entry when the step advances so the browser back button
	// can be intercepted to step back within the form instead of leaving the page.
	const prevStepRef = useRef( STEPS.PRODUCT );
	useEffect( () => {
		if ( step > prevStepRef.current && step < STEPS.SUCCESS ) {
			history.pushState( { ssfStep: step }, '' );
		}
		prevStepRef.current = step;
	}, [ step ] );

	useEffect( () => {
		function onPopState( e ) {
			if ( step > STEPS.PRODUCT && step < STEPS.SUCCESS ) {
				// Prevent the browser from actually navigating back.
				history.pushState( { ssfStep: step }, '' );
				dispatch( { type: 'BACK' } );
			}
		}
		window.addEventListener( 'popstate', onPopState );
		return () => window.removeEventListener( 'popstate', onPopState );
	}, [ step ] );

	return (
		<div className="ssf-form">
			{ showProgress && <ProgressBar step={ step } /> }
			<SelectionCrumbs state={ state } dispatch={ dispatch } />
			{ error && step !== STEPS.FIELDS && (
				<p className="ssf-error" role="alert">{ error }</p>
			) }
			{ step === STEPS.PRODUCT    && <StepProduct    products={ state.products } dispatch={ dispatch } /> }
			{ step === STEPS.ISSUE_TYPE && <StepIssueType  state={ state } dispatch={ dispatch } /> }
			{ step === STEPS.DESCRIBE   && <StepDescribe   state={ state } dispatch={ dispatch } /> }
			{ step === STEPS.DEFLECT    && <StepDeflect    state={ state } dispatch={ dispatch } /> }
			{ step === STEPS.FIELDS     && <StepFields     state={ state } dispatch={ dispatch } /> }
			{ step === STEPS.SUCCESS    && <StepSuccess    ticket={ state.ticket } /> }
		</div>
	);
}

// ── Mount ─────────────────────────────────────────────────────────────────────

document.querySelectorAll( '.ssf-form-root' ).forEach( ( el ) => {
	const products = JSON.parse( el.dataset.products || '[]' );
	createRoot( el ).render( <SupportForm products={ products } /> );
} );
